import cx from 'classnames';
import { Location } from 'history';
import { debounce } from 'lodash';
import { hasAds } from 'mk/bazaar/common/userUtils';
import { AD_UNITS, Collapse } from 'mk/ox/hbAdUnits';
import { ZoneTargeting } from 'mk2/containers/AdSlot/interfaces';
import { adSlotRefresh, adSlotRegister, adSlotUnregister } from 'mk2/containers/AdSlot/AdSlot.actions';
import { dbg, getMaxHeightWithAd } from 'mk2/containers/AdSlot/AdSlot.helpers';
import { AdSlotsState } from 'mk2/containers/AdSlot/AdSlot.reducers';
import { getAdSlotsState } from 'mk2/containers/AdSlot/AdSlot.selectors';
import { PREFIX_AD_SLOT_INTERSTITIAL } from 'mk2/containers/AdSlotInterstitial/AdSlotInterstitial';
import { MapDispatchToPropsObject } from 'mk2/helpers/types';
import { getRequestDeviceMobile, getRequestUser, AppState } from 'mk2/reducers';
import { UserEntity } from 'mk2/schemas';
import React from 'react';
import { connect } from 'react-redux';
import styles from './AdSlot.mscss';

// TODO: How to sync between server and client?
let dynamicAdCount = 0;
let registeredSlots: { [slotId: string]: { slotId: string, zone: string, targeting: ZoneTargeting }} = {};

const MARGIN_CLASSNAMES = {
    top: {
        3: 'AdSlot--marginTop3',
        10: 'AdSlot--marginTop10',
        20: 'AdSlot--marginTop20',
        30: 'AdSlot--marginTop30',
    },
    bottom: {
        3: 'AdSlot--marginBottom3',
        10: 'AdSlot--marginBottom10',
        20: 'AdSlot--marginBottom20',
        30: 'AdSlot--marginBottom30',
    },
};

export interface OwnProps {
    className?: string;
    style?: any;
    zone: string;
    targeting?: ZoneTargeting;
    prefix?: string;
    forceShowAds?: boolean;
    location: Location;
    children?: React.ReactNode;

    slotRef?: React.RefObject<HTMLDivElement>;
    withoutDiv?: boolean;

    // TODO: CeMi vyriesit krajsie, tak ze sa refactorne ako sa generuje slotId.
    // Ak budeme mat slotId v OwnProps, mozeme sa ist do reduxu opytat , ci sa nejaka
    // reklama zobrazila a podla toho vygenerovat DOM
    marginTopWithAd?: 3 | 10 | 20 | 30;
    marginBottomWithAd?: 3 | 10 | 20 | 30;

    allowDownscaling?: boolean;
    parentWidth?: number;
}

interface StateProps {
    requestUser: UserEntity;
    isMobile: boolean;
    adSlots: AdSlotsState;
}

interface DispatchProps {
    onRegister(slotId: string, zone: string, targeting: ZoneTargeting);
    onUnregister(slotId: string, zone: string);
    onRefresh(slotId: string);
}

type Props = OwnProps & StateProps & DispatchProps;

declare const window;

interface State {
    slotId: string;
    wrapperDivWidthWin: number;
    wrapperDivHeightWin: number;
}

export const compareForShowOnThisDevice = (isMobile, zone) => {
    return isMobile
        ? !zone.endsWith('(Desktop)')  // ignoruj Desktop-only plochy na mobile
        : !zone.endsWith('(Mobile)');  // ignoruj Mobile-only plochy na desktope
};

class AdSlotComponent extends React.PureComponent<Props, State> {
    public static defaultProps = {
        prefix: 'mk-slot',
    };

    private wrapperDivRef = React.createRef<HTMLDivElement>();

    private handleWrapperDivSizeWithGetBoundClientRect = debounce(() => {
        const el = this.wrapperDivRef.current;
        if (el) {
            const rect = el.getBoundingClientRect();
            const width = rect.width;
            const height = rect.height;

            const { wrapperDivWidthWin, wrapperDivHeightWin } = this.state;
            if (wrapperDivWidthWin !== width || wrapperDivHeightWin !== height) {
                this.setState({
                    wrapperDivWidthWin: width,
                    wrapperDivHeightWin: height,
                });
            }
        }
    }, 100);

    public constructor(props: Props) {
        super(props);

        const slotId = this.generateSlotId();
        this.state = {
            slotId,
            wrapperDivWidthWin: 0,
            wrapperDivHeightWin: 0,
        };

        if (process.env.SERVER) {
            registeredSlots[slotId] = {
                slotId,
                zone: props.zone,
                targeting: props.targeting,
            };
        }
    }

    public componentDidMount() {
        this.register();
        window.addEventListener('resize', this.handleWrapperDivSizeWithGetBoundClientRect);
        this.handleWrapperDivSizeWithGetBoundClientRect(); // hneď meraj aj pri mount
    }

    public UNSAFE_componentWillReceiveProps(nextProps: Props) {
        // Zone has changed - unregister
        // NOTE: Should we also test targeting (deep equal)? - currently no
        if (this.props.zone !== nextProps.zone) {
            this.unregister();

            const newSlotId = this.generateSlotId();
            if (this.state.slotId !== newSlotId) {
                this.setState({
                    slotId: newSlotId,
                });
            }
        }
    }

    public componentDidUpdate(prevProps: Props) {
        // If interstitial, don't re-register / refresh
        if (this.props.prefix === PREFIX_AD_SLOT_INTERSTITIAL) {
            return;
        }

        // Zone has changed - register
        // NOTE: Should we also test targeting (deep equal)? - currently no
        if (prevProps.zone !== this.props.zone) {
            this.register();
        }

        if (!prevProps.location || !this.props.location || prevProps.location.key !== this.props.location.key) {
            this.refresh();
        }

        this.handleWrapperDivSizeWithGetBoundClientRect();
    }

    public componentWillUnmount() {
        window.removeEventListener('resize', this.handleWrapperDivSizeWithGetBoundClientRect);
        this.handleWrapperDivSizeWithGetBoundClientRect.cancel(); // zruší debounce pri unmount
        this.unregister();
    }

    public render() {
        const {
            className, forceShowAds, isMobile, requestUser, children, marginTopWithAd, marginBottomWithAd, zone,
            withoutDiv, adSlots, parentWidth, allowDownscaling,
        } = this.props;

        const slotId = this.state.slotId;

        if (!this.showOnThisDevice() || (!forceShowAds && !hasAds(requestUser))) {
            return null;
        }

        const adSlot = adSlots[slotId];
        const adSlotLastSize = adSlot?.lastSize;
        const { adSlotWidth, adSlotHeight } = sanitizeSize(adSlotLastSize);
        const hasSize = (
            Boolean(adSlotLastSize) &&
            adSlotWidth && adSlotWidth > 1 &&
            adSlotHeight && adSlotHeight > 1
        );

        // let downscale a branding with buggy size
        const buggyBranding = (
            zone.includes('Branding (Desktop)') &&
            adSlotWidth && adSlotHeight &&
            1020 < adSlotWidth && adSlotWidth <= 1339 &&
            250 < adSlotHeight && adSlotHeight <= 345
        );

        let stylesForScaledWrapper = {};
        let stylesForScaledAd = {};
        const wrapperWidth = parentWidth || this.state.wrapperDivWidthWin;
        if (allowDownscaling || buggyBranding) {
            const { scaledWidth, scaledHeight, scale } = getDownscaledSize(
                wrapperWidth,
                adSlotWidth,
                adSlotHeight + (marginTopWithAd || 0) + (marginBottomWithAd || 0),
            );
            let transformOrigin = 'center';
            if (buggyBranding) {
                transformOrigin = 'center';
            }
            if (adSlotWidth && adSlotHeight && scale && scale !== 1) {
                stylesForScaledWrapper = {
                    overflow: 'hidden',
                    // minWidth: '100%',
                    height: `${scaledHeight}px`,
                    width: `${scaledWidth}px`,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                };
                stylesForScaledAd = {
                    width: `${adSlotWidth}px`,
                    height: `${adSlotHeight}px`,
                    transform: `scale(${scale})`,
                    transformOrigin,
                };
            }
        }

        const adUnit = AD_UNITS[zone];

        const maxHeightWithAd = getMaxHeightWithAd(adUnit, isMobile);
        let minHeight: number;
        if (
            !hasSize &&
            (adUnit.collapse === Collapse.NEVER || adUnit.collapse === Collapse.AFTER_FETCH)
        ) {
            minHeight = (
                (marginTopWithAd || 0) +
                (maxHeightWithAd || 0) +
                (marginBottomWithAd || 0)
            );
        }

        if (withoutDiv) {
            return children || null;
        }

        return (
            <div
                style={stylesForScaledWrapper}
                ref={this.wrapperDivRef}
                className={className}
            >
                <div
                    style={stylesForScaledAd}
                    className={styles.AdSlot__wrapper}
                >
                    <div
                        suppressHydrationWarning
                        id={slotId}
                        className={cx(
                            styles.AdSlot,
                            // apply margins and min height on inner div (if ad filled)
                            marginTopWithAd && styles[MARGIN_CLASSNAMES.top[marginTopWithAd]],
                            marginBottomWithAd && styles[MARGIN_CLASSNAMES.bottom[marginBottomWithAd]],
                        )}
                        ref={this.props.slotRef}
                        style={{
                            // apply min height on current div to show a placeholder gap till ad (inner div) is unfilled
                            minHeight,
                            ...this.props.style,
                        }}
                    >
                        {children}
                    </div>
                </div>
            </div>
        );
    }

    private refresh() {
        const { onRefresh, forceShowAds, requestUser } = this.props;

        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            onRefresh(this.state.slotId);
        }
    }

    private showOnThisDevice() {
        const { isMobile, zone } = this.props;
        return compareForShowOnThisDevice(isMobile, zone);
    }

    private generateSlotId = () => {
        return `${this.props.prefix}-${dynamicAdCount++}`;
    };

    private register = () => {
        const { zone, targeting, onRegister, forceShowAds, requestUser } = this.props;
        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            dbg(`[ads:dbg] AdSlot register:onRegister`);
            onRegister(this.state.slotId, zone, targeting);
        }
    };

    private unregister = () => {
        const { zone, onUnregister, forceShowAds, requestUser } = this.props;
        if (this.showOnThisDevice() && (forceShowAds || hasAds(requestUser))) {
            onUnregister(this.state.slotId, zone);
        }
    };

}

function mapStateToProps(state: AppState): StateProps {
    return {
        requestUser: getRequestUser(state),
        isMobile: getRequestDeviceMobile(state),
        adSlots: getAdSlotsState(state),
    };
}

const mapDispatchToProps: MapDispatchToPropsObject<DispatchProps> = {
    onRegister: adSlotRegister,
    onUnregister: adSlotUnregister,
    onRefresh: adSlotRefresh,
};

export const AdSlot = connect(mapStateToProps, mapDispatchToProps)(AdSlotComponent);

export function resetAdCount() {
    dynamicAdCount = 0;
}

export function clearRegisteredSlots() {
    registeredSlots = {};
}

export function getRegisteredSlots() {
    return registeredSlots;
}

export function sanitizeSize(size: number[] | string) {
    let adSlotWidth = null;
    let adSlotHeight = null;
    if (size) {
        if (Array.isArray(size)) {
            adSlotWidth = size[0];
            adSlotHeight = size[1];
        } else if (typeof size === 'string') {
            const sizeParts = size.split('x');
            if (sizeParts.length === 2) {
                adSlotWidth = parseInt(sizeParts[0], 10);
                adSlotHeight = parseInt(sizeParts[1], 10);
            }
        }
    }
    return {
        adSlotWidth,
        adSlotHeight,
    };
}

export function getDownscaledSize(parentWidth: number, adSlotWidth: number, adSlotHeight: number): { scale: number, scaledWidth: number, scaledHeight: number } {
    let scale = 1;
    if (parentWidth && adSlotWidth) {
        const rawScale = parentWidth / adSlotWidth;
        scale = isFinite(rawScale) && rawScale > 0 && rawScale < 1 ? rawScale : 1;
    }

    return {
        scale,
        scaledWidth: Math.round(adSlotWidth * scale),
        scaledHeight: Math.round(adSlotHeight * scale),
    };
}
